(function () {
  'use strict';
 
  if (window.UnifiedProviderManager) {
    return;
  }
 
  const VERSION = '1.1.0';
 
  const LEGACY_DEBUG_EVENT_NAME =
    'unified:debug';
 
  const MAX_HISTORY = 100;

  const GA4_ALLOWED_ANALYTICS = new Set([
    'ga4_standard',
    'dimension',
    'metric'
  ]);
 
  const PROVIDER_ID_PATTERN =
    /^[a-z][a-z0-9_-]*$/;
 
  const registry = new Map();
 
  let dispatchSequence = 0;
  let dispatchHistory = [];
 
  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }
 
  function isDebugEnabled() {
    return getConfig().debug === true;
  }
 
  function isPlainObject(value) {
    return (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value)
    );
  }
 
  function clone(value) {
    if (
      value === null ||
      typeof value === 'undefined'
    ) {
      return value;
    }
 
    try {
      return JSON.parse(
        JSON.stringify(value)
      );
    } catch (error) {
      return value;
    }
  }
 
  function uniqueStrings(values) {
    const result = [];
    const seen = new Set();
 
    (Array.isArray(values)
      ? values
      : [values]
    ).forEach(function (value) {
      if (typeof value !== 'string') {
        return;
      }
 
      const normalized =
        value.trim().toLowerCase();
 
      if (
        !normalized ||
        seen.has(normalized)
      ) {
        return;
      }
 
      seen.add(normalized);
      result.push(normalized);
    });
 
    return result;
  }
 
  function nowIso() {
    return new Date().toISOString();
  }
 
  function performanceStart(
    name,
    metadata
  ) {
    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance
        .start === 'function'
    ) {
      try {
        return window.UnifiedPerformance
          .start(
            name,
            metadata || {}
          );
      } catch (error) {
        return null;
      }
    }
 
    return null;
  }
 
  function performanceEnd(
    token,
    metadata
  ) {
    if (
      !token ||
      !window.UnifiedPerformance ||
      typeof window.UnifiedPerformance
        .end !== 'function'
    ) {
      return null;
    }
 
    try {
      return window.UnifiedPerformance
        .end(
          token,
          metadata || {}
        );
    } catch (error) {
      return null;
    }
  }
 
  function reportError(
    severity,
    code,
    message,
    details
  ) {
    if (
      window.UnifiedErrors &&
      typeof window.UnifiedErrors
        .report === 'function'
    ) {
      try {
        return window.UnifiedErrors
          .report(
            severity,
            code,
            message,
            details || {}
          );
      } catch (error) {
        // Continue with console fallback.
      }
    }
 
    const method =
      severity === 'error' ||
      severity === 'fatal'
        ? 'error'
        : 'warn';
 
    if (
      window.console &&
      typeof window.console[method] ===
        'function'
    ) {
      window.console[method](
        '[Unified Provider Manager] ' +
          message,
        details || {}
      );
    }
 
    return null;
  }
 
  function emitEvent(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      return window.UnifiedBus.emit(
        type,
        data || {}
      );
    }
 
    const detail = {
      type: type,
      timestamp: nowIso(),
      data: data || {}
    };
 
    try {
      window.dispatchEvent(
        new CustomEvent(
          LEGACY_DEBUG_EVENT_NAME,
          {
            detail: detail
          }
        )
      );
    } catch (error) {
      // Event fallback is best effort only.
    }
 
    return detail;
  }
 
  function debugLog(
    message,
    data
  ) {
    if (!isDebugEnabled()) {
      return;
    }
 
    console.info(
      '[Unified Provider Manager] ' +
        message,
      data || {}
    );
  }
 
  function getProviderConfig(
    providerId
  ) {
    const config = getConfig();
 
    if (
      !config.providers ||
      typeof config.providers[
        providerId
      ] === 'undefined'
    ) {
      return undefined;
    }
 
    return config.providers[
      providerId
    ];
  }
 
  function getConfigEnabledState(
    providerId
  ) {
    const providerConfig =
      getProviderConfig(providerId);
 
    if (
      typeof providerConfig ===
      'boolean'
    ) {
      return providerConfig;
    }
 
    if (isPlainObject(providerConfig)) {
      return providerConfig.enabled !== false;
    }
 
    return undefined;
  }
 
  function normalizeAdapter(
    adapter
  ) {
    if (typeof adapter === 'function') {
      return {
        send: adapter
      };
    }
 
    if (!isPlainObject(adapter)) {
      return null;
    }
 
    return adapter;
  }
 
  function validateProviderId(
    providerId
  ) {
    return (
      typeof providerId === 'string' &&
      PROVIDER_ID_PATTERN.test(
        providerId
      )
    );
  }
 
  function registerProvider(
    providerId,
    adapter,
    options
  ) {
    const normalizedId =
      typeof providerId === 'string'
        ? providerId
            .trim()
            .toLowerCase()
        : '';
 
    const normalizedAdapter =
      normalizeAdapter(adapter);
 
    const sourceOptions =
      isPlainObject(options)
        ? options
        : {};
 
    if (!validateProviderId(normalizedId)) {
      return {
        success: false,
        provider: normalizedId,
        error: {
          code: 'provider_id_invalid',
          message:
            'Provider id must match ' +
            PROVIDER_ID_PATTERN.toString()
        }
      };
    }
 
    if (
      !normalizedAdapter ||
      typeof normalizedAdapter.send !==
        'function'
    ) {
      return {
        success: false,
        provider: normalizedId,
        error: {
          code: 'provider_adapter_invalid',
          message:
            'Provider adapter must expose send().'
        }
      };
    }
 
    if (
      registry.has(normalizedId) &&
      sourceOptions.replace !== true
    ) {
      return {
        success: false,
        provider: normalizedId,
        error: {
          code: 'provider_already_registered',
          message:
            'Provider is already registered.'
        }
      };
    }
 
    const registeredAt = nowIso();
 
    const record = {
      id: normalizedId,
 
      name:
        typeof sourceOptions.name ===
          'string' &&
        sourceOptions.name.trim()
          ? sourceOptions.name.trim()
          : normalizedId,
 
      adapter: normalizedAdapter,
 
      enabled:
        typeof sourceOptions.enabled ===
          'boolean'
          ? sourceOptions.enabled
          : null,
 
      priority:
        Number.isFinite(
          Number(sourceOptions.priority)
        )
          ? Number(sourceOptions.priority)
          : 100,
 
      metadata:
        isPlainObject(
          sourceOptions.metadata
        )
          ? clone(
              sourceOptions.metadata
            )
          : {},
 
      registered_at: registeredAt
    };
 
    registry.set(
      normalizedId,
      record
    );
 
    emitEvent(
      'provider_registered',
      {
        provider: normalizedId,
        name: record.name,
        priority: record.priority,
        enabled:
          isProviderEnabled(
            normalizedId
          )
      }
    );
 
    debugLog(
      'Provider registered',
      {
        provider: normalizedId,
        enabled:
          isProviderEnabled(
            normalizedId
          )
      }
    );
 
    return {
      success: true,
      provider: normalizedId,
      registered_at: registeredAt
    };
  }
 
  function unregisterProvider(
    providerId
  ) {
    const normalizedId =
      typeof providerId === 'string'
        ? providerId
            .trim()
            .toLowerCase()
        : '';
 
    if (!registry.has(normalizedId)) {
      return false;
    }
 
    registry.delete(normalizedId);
 
    emitEvent(
      'provider_unregistered',
      {
        provider: normalizedId
      }
    );
 
    return true;
  }
 
  function isProviderEnabled(
    providerId
  ) {
    const record =
      registry.get(providerId);
 
    if (!record) {
      return false;
    }
 
    if (
      typeof record.enabled ===
      'boolean'
    ) {
      return record.enabled;
    }
 
    const configState =
      getConfigEnabledState(
        providerId
      );
 
    return configState === true;
  }
 
  function isProviderAvailable(
    providerId
  ) {
    const record =
      registry.get(providerId);
 
    if (!record) {
      return false;
    }
 
    if (
      typeof record.adapter
        .isAvailable !== 'function'
    ) {
      return true;
    }
 
    try {
      return (
        record.adapter
          .isAvailable() === true
      );
    } catch (error) {
      return false;
    }
  }
 
  function getProviderStatus(
    providerId
  ) {
    const normalizedId =
      typeof providerId === 'string'
        ? providerId
            .trim()
            .toLowerCase()
        : '';
 
    const record =
      registry.get(normalizedId);
 
    if (!record) {
      return null;
    }
 
    return {
      id: record.id,
      name: record.name,
      enabled:
        isProviderEnabled(
          normalizedId
        ),
      available:
        isProviderAvailable(
          normalizedId
        ),
      priority: record.priority,
      metadata: clone(record.metadata),
      registered_at:
        record.registered_at
    };
  }
 
  function getProvider(
    providerId
  ) {
    return getProviderStatus(
      providerId
    );
  }
 
  function getProviders() {
    return Array.from(
      registry.keys()
    )
      .map(function (providerId) {
        return getProviderStatus(
          providerId
        );
      })
      .sort(function (left, right) {
        if (
          left.priority !==
          right.priority
        ) {
          return (
            right.priority -
            left.priority
          );
        }
 
        return left.id.localeCompare(
          right.id
        );
      });
  }
 
  function getEnabledProviders() {
    return getProviders()
      .filter(function (provider) {
        return provider.enabled === true;
      });
  }
 
  function getDefaultRoutes() {
    return getEnabledProviders()
      .map(function (provider) {
        return provider.id;
      });
  }
 
  function resolveRoutes(
    requestedRoutes
  ) {
    const normalizedRequested =
      uniqueStrings(
        requestedRoutes || []
      );
 
    const explicit =
      normalizedRequested.length > 0;
 
    const candidates = explicit
      ? normalizedRequested
      : getDefaultRoutes();
 
    const resolved = [];
    const skipped = [];
    const warnings = [];
 
    candidates.forEach(
      function (providerId) {
        if (!registry.has(providerId)) {
          const warning = {
            code: 'provider_unknown',
            provider: providerId
          };
 
          skipped.push(
            Object.assign(
              {
                reason:
                  'provider_unknown'
              },
              warning
            )
          );
 
          warnings.push(warning);
          return;
        }
 
        if (
          !isProviderEnabled(
            providerId
          )
        ) {
          const warning = {
            code: 'provider_disabled',
            provider: providerId
          };
 
          skipped.push(
            Object.assign(
              {
                reason:
                  'provider_disabled'
              },
              warning
            )
          );
 
          warnings.push(warning);
          return;
        }
 
        if (
          !isProviderAvailable(
            providerId
          )
        ) {
          const warning = {
            code:
              'provider_unavailable',
            provider: providerId
          };
 
          skipped.push(
            Object.assign(
              {
                reason:
                  'provider_unavailable'
              },
              warning
            )
          );
 
          warnings.push(warning);
          return;
        }
 
        resolved.push(providerId);
      }
    );
 
    if (!resolved.length) {
      warnings.push({
        code: 'no_provider_resolved',
        requested_routes:
          normalizedRequested,
        route_source:
          explicit
            ? 'explicit'
            : 'default'
      });
    }
 
    return {
      explicit: explicit,
      route_source:
        explicit
          ? 'explicit'
          : 'default',
      requested_routes:
        normalizedRequested,
      candidate_routes:
        candidates.slice(),
      resolved_routes: resolved,
      skipped: skipped,
      warnings: warnings
    };
  }
 
  function normalizeSendResult(
    rawResult
  ) {
    if (
      rawResult &&
      typeof rawResult.then ===
        'function'
    ) {
      return {
        success: false,
        code:
          'async_provider_unsupported',
        message:
          'Provider adapters must return synchronously.'
      };
    }
 
    if (rawResult === true) {
      return {
        success: true
      };
    }
 
    if (
      rawResult === false ||
      rawResult === null ||
      typeof rawResult ===
        'undefined'
    ) {
      return {
        success: false,
        code: 'provider_send_failed',
        message:
          'Provider returned an unsuccessful result.'
      };
    }
 
    if (isPlainObject(rawResult)) {
      return Object.assign(
        {},
        rawResult,
        {
          success:
            rawResult.success === true
        }
      );
    }
 
    return {
      success: false,
      code:
        'provider_result_invalid',
      message:
        'Provider returned an unsupported result.'
    };
  }
 
  function sendToProvider(
    providerId,
    eventName,
    payload,
    context
  ) {
    const record =
      registry.get(providerId);
 
    const startedAt =
      typeof performance !==
        'undefined' &&
      typeof performance.now ===
        'function'
        ? performance.now()
        : Date.now();
 
    const performanceToken =
      performanceStart(
        'provider.' + providerId,
        {
          provider: providerId,
          event: eventName
        }
      );
 
    try {
      const rawResult =
        record.adapter.send(
          eventName,
          payload,
          context || {}
        );
 
      const normalized =
        normalizeSendResult(
          rawResult
        );
 
      const endedAt =
        typeof performance !==
          'undefined' &&
        typeof performance.now ===
          'function'
          ? performance.now()
          : Date.now();
 
      const durationMs = Math.max(
        0,
        endedAt - startedAt
      );
 
      if (normalized.success) {
        performanceEnd(
          performanceToken,
          {
            status: 'sent',
            duration_ms:
              durationMs
          }
        );
 
        const result = {
          provider: providerId,
          status: 'sent',
          success: true,
          duration_ms:
            Number(
              durationMs.toFixed(3)
            ),
          details: clone(normalized)
        };
 
        emitEvent(
          'provider_sent',
          {
            provider: providerId,
            event: eventName,
            status: 'sent',
            duration_ms:
              result.duration_ms,
            details:
              clone(normalized)
          }
        );
 
        return result;
      }
 
      performanceEnd(
        performanceToken,
        {
          status: 'failed',
          code:
            normalized.code ||
            'provider_send_failed'
        }
      );
 
      const failedResult = {
        provider: providerId,
        status: 'failed',
        success: false,
        code:
          normalized.code ||
          'provider_send_failed',
        message:
          normalized.message ||
          'Provider dispatch failed.',
        duration_ms:
          Number(
            durationMs.toFixed(3)
          ),
        details: clone(normalized)
      };
 
      emitEvent(
        'provider_failed',
        {
          provider: providerId,
          event: eventName,
          status: 'failed',
          code: failedResult.code,
          message:
            failedResult.message,
          duration_ms:
            failedResult.duration_ms
        }
      );
 
      reportError(
        'warning',
        failedResult.code,
        'Provider dispatch failed',
        {
          provider: providerId,
          event: eventName,
          message:
            failedResult.message
        }
      );
 
      return failedResult;
    } catch (error) {
      performanceEnd(
        performanceToken,
        {
          status: 'error'
        }
      );
 
      const errorResult = {
        provider: providerId,
        status: 'failed',
        success: false,
        code: 'provider_exception',
        message:
          error && error.message
            ? error.message
            : 'Provider threw an exception.'
      };
 
      emitEvent(
        'provider_failed',
        {
          provider: providerId,
          event: eventName,
          status: 'failed',
          code: errorResult.code,
          message:
            errorResult.message
        }
      );
 
      reportError(
        'error',
        errorResult.code,
        'Provider adapter threw an exception',
        {
          provider: providerId,
          event: eventName,
          error:
            errorResult.message
        }
      );
 
      return errorResult;
    }
  }
 
  function addDispatchHistory(
    result
  ) {
    dispatchHistory.push(
      clone(result)
    );
 
    if (
      dispatchHistory.length >
      MAX_HISTORY
    ) {
      dispatchHistory =
        dispatchHistory.slice(
          dispatchHistory.length -
          MAX_HISTORY
        );
    }
  }
 
  function dispatch(
    eventName,
    payload,
    options
  ) {
    const performanceToken =
      performanceStart(
        'providers.dispatch',
        {
          event: eventName || ''
        }
      );
 
    const sourceOptions =
      isPlainObject(options)
        ? options
        : {};
 
    const result = {
      id:
        'provider-dispatch-' +
        String(++dispatchSequence),
      success: false,
      delivered: false,
      partial: false,
      event:
        typeof eventName === 'string'
          ? eventName
          : '',
      route_source: 'default',
      requested_routes: [],
      resolved_routes: [],
      providers: {},
      sent_count: 0,
      failed_count: 0,
      skipped_count: 0,
      warnings: [],
      errors: [],
      dispatched_at: nowIso()
    };
 
    if (
      typeof eventName !== 'string' ||
      !eventName.trim()
    ) {
      result.errors.push({
        code:
          'provider_event_name_missing'
      });
 
      performanceEnd(
        performanceToken,
        {
          status: 'invalid'
        }
      );
 
      addDispatchHistory(result);
      return result;
    }
 
    if (!isPlainObject(payload)) {
      result.errors.push({
        code:
          'provider_payload_invalid'
      });
 
      performanceEnd(
        performanceToken,
        {
          status: 'invalid'
        }
      );
 
      addDispatchHistory(result);
      return result;
    }
 
    const routeResolution =
      resolveRoutes(
        sourceOptions.routes || []
      );
 
    result.route_source =
      routeResolution.route_source;
 
    result.requested_routes =
      routeResolution
        .requested_routes;
 
    result.resolved_routes =
      routeResolution
        .resolved_routes;
 
    result.warnings =
      routeResolution.warnings
        .slice();
 
    routeResolution.skipped
      .forEach(function (skipped) {
        result.providers[
          skipped.provider
        ] = {
          provider:
            skipped.provider,
          status: 'skipped',
          success: false,
          reason: skipped.reason
        };
 
        result.skipped_count += 1;
 
        emitEvent(
          'provider_skipped',
          {
            provider:
              skipped.provider,
            event: eventName,
            reason: skipped.reason
          }
        );
      });
 
    routeResolution.resolved_routes
      .forEach(function (providerId) {
        const providerResult =
          sendToProvider(
            providerId,
            eventName,
            payload,
            {
              dispatch_id: result.id,
              route_source:
                result.route_source,
              requested_routes:
                result.requested_routes,
              metadata:
                isPlainObject(
                  sourceOptions.metadata
                )
                  ? clone(
                      sourceOptions
                        .metadata
                    )
                  : {}
            }
          );
 
        result.providers[
          providerId
        ] = providerResult;
 
        if (
          providerResult.success ===
          true
        ) {
          result.sent_count += 1;
        } else {
          result.failed_count += 1;
 
          result.errors.push({
            code:
              providerResult.code ||
              'provider_send_failed',
            provider: providerId,
            message:
              providerResult.message ||
              'Provider dispatch failed.'
          });
        }
      });
 
    result.delivered =
      result.sent_count > 0;
 
    result.success =
      result.delivered === true &&
      result.failed_count === 0;
 
    result.partial =
      result.delivered === true &&
      (
        result.failed_count > 0 ||
        result.skipped_count > 0
      );
 
    emitEvent(
      'providers_dispatched',
      {
        dispatch_id: result.id,
        event: eventName,
        success: result.success,
        delivered:
          result.delivered,
        partial: result.partial,
        route_source:
          result.route_source,
        requested_routes:
          result.requested_routes,
        resolved_routes:
          result.resolved_routes,
        sent_count:
          result.sent_count,
        failed_count:
          result.failed_count,
        skipped_count:
          result.skipped_count,
        warning_count:
          result.warnings.length,
        error_count:
          result.errors.length
      }
    );
 
    performanceEnd(
      performanceToken,
      {
        status:
          result.delivered
            ? result.partial
              ? 'partial'
              : 'delivered'
            : 'not_delivered',
        sent_count:
          result.sent_count,
        failed_count:
          result.failed_count,
        skipped_count:
          result.skipped_count
      }
    );
 
    addDispatchHistory(result);
 
    debugLog(
      'Providers dispatched',
      {
        event: eventName,
        delivered:
          result.delivered,
        partial: result.partial,
        sent:
          result.sent_count,
        failed:
          result.failed_count,
        skipped:
          result.skipped_count
      }
    );
 
    return clone(result);
  }
 
  function getDispatchHistory() {
    return dispatchHistory.map(
      function (entry) {
        return clone(entry);
      }
    );
  }
 
  function clearDispatchHistory() {
    dispatchHistory = [];
    dispatchSequence = 0;
 
    return true;
  }
 
  function getStats() {
    const stats = {
      registered:
        registry.size,
      enabled: 0,
      available: 0,
      dispatches:
        dispatchHistory.length,
      delivered: 0,
      partial: 0,
      failed: 0
    };
 
    getProviders().forEach(
      function (provider) {
        if (provider.enabled) {
          stats.enabled += 1;
        }
 
        if (provider.available) {
          stats.available += 1;
        }
      }
    );
 
    dispatchHistory.forEach(
      function (entry) {
        if (entry.delivered) {
          stats.delivered += 1;
        } else {
          stats.failed += 1;
        }
 
        if (entry.partial) {
          stats.partial += 1;
        }
      }
    );
 
    return stats;
  }
 
  function projectGa4Payload(
    eventName,
    payload
  ) {
    if (!isPlainObject(payload)) {
      return {
        success: false,
        code:
          'ga4_payload_invalid',
        message:
          'GA4 payload must be a plain object.',
        payload: {}
      };
    }

    const schema =
      window.UnifiedSchema || null;

    if (
      !schema ||
      typeof schema
        .getAnalyticsMap !==
        'function'
    ) {
      return {
        success: false,
        code:
          'ga4_schema_unavailable',
        message:
          'UnifiedSchema.getAnalyticsMap() is required for GA4 projection.',
        payload: {}
      };
    }

    const analyticsMap =
      schema.getAnalyticsMap(
        eventName
      );

    if (
      !isPlainObject(
        analyticsMap
      )
    ) {
      return {
        success: false,
        code:
          'ga4_analytics_map_invalid',
        message:
          'GA4 analytics map is unavailable or invalid.',
        payload: {}
      };
    }

    const projectedPayload = {};
    const excludedFields = [];

    Object.keys(payload)
      .forEach(function (fieldName) {
        const classification =
          analyticsMap[
            fieldName
          ] || 'unknown';

        if (
          GA4_ALLOWED_ANALYTICS
            .has(
              classification
            )
        ) {
          projectedPayload[
            fieldName
          ] =
            payload[
              fieldName
            ];

          return;
        }

        excludedFields.push({
          field:
            fieldName,
          classification:
            classification
        });
      });

    return {
      success: true,
      payload:
        projectedPayload,
      projected_fields:
        Object.keys(
          projectedPayload
        ),
      excluded_fields:
        excludedFields
    };
  }

  function registerBuiltInProviders() {
    registerProvider(
      'ga4',
      {
        isAvailable: function () {
          return (
            typeof window.gtag ===
            'function'
          );
        },
 
        send: function (
          eventName,
          payload
        ) {
          if (
            typeof window.gtag !==
            'function'
          ) {
            return {
              success: false,
              code:
                'ga4_unavailable',
              message:
                'window.gtag is not available.'
            };
          }

          const projection =
            projectGa4Payload(
              eventName,
              payload
            );

          if (
            projection.success !==
            true
          ) {
            return {
              success: false,
              code:
                projection.code ||
                'ga4_projection_failed',
              message:
                projection.message ||
                'GA4 payload projection failed.'
            };
          }

          window.gtag(
            'event',
            eventName,
            projection.payload
          );

          return {
            success: true,
            transport:
              'gtag',
            projected_fields:
              projection
                .projected_fields,
            excluded_fields:
              projection
                .excluded_fields
          };
        }
      },
      {
        name:
          'Google Analytics 4',
        priority: 1000,
        metadata: {
          built_in: true,
          transport: 'gtag'
        }
      }
    );
  }
 
  window.UnifiedProviderManager = {
    version: VERSION,
 
    registerProvider:
      registerProvider,
 
    unregisterProvider:
      unregisterProvider,
 
    getProvider: getProvider,
 
    getProviders: getProviders,
 
    getProviderStatus:
      getProviderStatus,
 
    getEnabledProviders:
      getEnabledProviders,
 
    getDefaultRoutes:
      getDefaultRoutes,
 
    resolveRoutes: resolveRoutes,
 
    dispatch: dispatch,
 
    getDispatchHistory:
      getDispatchHistory,
 
    clearDispatchHistory:
      clearDispatchHistory,
 
    getStats: getStats
  };
 
  registerBuiltInProviders();
 
  if (isDebugEnabled()) {
    console.info(
      '[Unified Provider Manager] Provider Manager initialized',
      {
        version: VERSION,
        providers:
          getProviders()
      }
    );
  }
})();